import {ChangeDetectionStrategy, Component, forwardRef, Input, OnDestroy, inject} from '@angular/core';
import {AbstractControl, ControlValueAccessor, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, 
  NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, ValidatorFn, Validators, ReactiveFormsModule} from '@angular/forms';
import {Subscription} from 'rxjs';
import moment from 'moment';
import {ADDRESS_PATTERN, EMPLOYER_PATTERN, PERSON_NAME_PATTERN, POSTAL_PATTERN} from '../../_constants/constants';
import {phoneValidator, sinValidator} from '../../_utils/validators';
import {clearErrors, valueExists} from '../../_utils/forms';
import {I18nService} from '../../_services/gui/i18n.service';
import {Profile} from '../../_data/reports/business/request/business-services-order-data';
import {ValidationErrorsComponent as ValidationErrorsComponent_1} from '../../common/forms/validation-errors/validation-errors.component';
import {HideOnBlurDirective} from '../../_directives/hide-on-blur.directive';
import {NgxMaskModule} from 'ngx-mask';
import {KeyValuePipe} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';

/**
 * @author Prakash
 */
@Component({
    selector: 'app-owner-profile',
    templateUrl: './owner-profile.component.html',
    styleUrls: ['./owner-profile.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => OwnerProfileComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => OwnerProfileComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ReactiveFormsModule, ValidationErrorsComponent_1, HideOnBlurDirective, NgxMaskModule, KeyValuePipe, TranslateModule]
})
export class OwnerProfileComponent implements ControlValueAccessor, OnDestroy {

  /**
   * This gets passed to the nested validation-error components.
   *
   * @see {@link ValidationErrorsComponent.displayErrors}
   */
  @Input() displayErrors: boolean = false;

  form: UntypedFormGroup;
  subscriptions: Subscription[] = [];
  provinceNames: Map<string, string>;
  cardTypes: Map<string, string>;

  private formBuilder = inject(UntypedFormBuilder);
  private i18nService = inject(I18nService);
  
  constructor() {
    const i18nService = this.i18nService;

    this.form = this.formBuilder.group({
      firstName:        ['', Validators.compose([Validators.maxLength(16), Validators.pattern(PERSON_NAME_PATTERN)])],
      middleName:       ['', Validators.compose([Validators.maxLength(10), Validators.pattern(PERSON_NAME_PATTERN)])],
      lastName:         ['', Validators.compose([Validators.maxLength(20), Validators.pattern(PERSON_NAME_PATTERN)])],
      sin:              '',
      dob:              '',
      card:             '',
      account:          '',
      streetNum:        ['', Validators.compose([Validators.maxLength(10), Validators.pattern(ADDRESS_PATTERN)])],
      street:           ['', Validators.compose([Validators.maxLength(48), Validators.pattern(ADDRESS_PATTERN)])],
      apartmentNum:     ['', Validators.compose([Validators.maxLength(5), Validators.pattern(ADDRESS_PATTERN)])],
      city:             ['', Validators.compose([Validators.maxLength(33), Validators.pattern(ADDRESS_PATTERN)])],
      province:         '',
      postalCode:       ['', Validators.compose([Validators.minLength(6), Validators.maxLength(6), Validators.pattern(POSTAL_PATTERN)])],
      inclPrevAddress:  '',
      prevStreetNum:    ['', Validators.compose([Validators.maxLength(10), Validators.pattern(ADDRESS_PATTERN)])],
      prevStreet:       ['', Validators.compose([Validators.maxLength(48), Validators.pattern(ADDRESS_PATTERN)])],
      prevApartmentNum: ['', Validators.compose([Validators.maxLength(5), Validators.pattern(ADDRESS_PATTERN)])],
      prevCity:         ['', Validators.compose([Validators.maxLength(33), Validators.pattern(ADDRESS_PATTERN)])],
      prevProvince:     '',
      prevPostalCode:   ['', Validators.compose([Validators.minLength(6), Validators.maxLength(6), Validators.pattern(POSTAL_PATTERN)])],
      phone:            ['', Validators.compose([Validators.maxLength(16), phoneValidator])],
      job:              ['', Validators.compose([Validators.maxLength(30), Validators.pattern(ADDRESS_PATTERN)])],
      employer:         ['', Validators.compose([Validators.maxLength(35), Validators.pattern(EMPLOYER_PATTERN)])]
    }, {validators: this.getProfileValidators()});

    i18nService.provinceNames.subscribe(value => this.provinceNames = value);
    i18nService.cardTypes.subscribe(value => this.cardTypes = value);
    this.subscriptions.push(
      this.form.valueChanges.subscribe(value => {
        try {
          this.onChange(value);
          this.onTouched();
        } catch(e) {
          console.debug('Profile Object is removed');
        }
      })
    );
  }

  private getProfileValidators(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const firstName = group.get('firstName');
      const lastName = group.get('lastName');
      if(!valueExists(firstName)) {
        firstName.setErrors({required: true});
      }
      if(!valueExists(lastName)) {
        lastName.setErrors({required: true});
      }
      const street = group.get('street');
      const city = group.get('city');
      const province = group.get('province');
      const postalCode = group.get('postalCode');
      if(!valueExists(street)) {
        street.setErrors({required: true});
      }
      if(!valueExists(city)) {
        city.setErrors({required: true});
      }
      if(!valueExists(province)) {
        province.setErrors({required: true});
      }
      if(!valueExists(postalCode)) {
        postalCode.setErrors({required: true});
      }
      const prevStreet = group.get('prevStreet');
      const prevCity = group.get('prevCity');
      const prevProvince = group.get('prevProvince');
      const prevPostalCode = group.get('prevPostalCode');
      const inclPrevAddress = group.get('inclPrevAddress');
      if(!inclPrevAddress?.value) {
        clearErrors(prevStreet);
        clearErrors(prevCity);
        clearErrors(prevProvince);
        clearErrors(prevPostalCode);
      } else {
        if(inclPrevAddress?.value && !valueExists(prevStreet)) {
          prevStreet.setErrors({required: true});
        }
        if(inclPrevAddress?.value && !valueExists(prevCity)) {
          prevCity.setErrors({required: true});
        }
        if(inclPrevAddress?.value && !valueExists(prevProvince)) {
          prevProvince.setErrors({required: true});
        }
        if(inclPrevAddress?.value && !valueExists(prevPostalCode)) {
          prevPostalCode.setErrors({required: true});
        }
      }
      const sin = group.get('sin');
      const dob = group.get('dob');
      const card = group.get('card');
      const account = group.get('account');
      if(!valueExists(sin) && !valueExists(dob) && (!valueExists(card) && !valueExists(account))) {
        sin.setErrors({oneOf: true});
        dob.setErrors({oneOf: true});
        account.setErrors({oneOf: true});
      } else if(valueExists(sin)) {
        clearErrors(sin);
        clearErrors(dob);
        clearErrors(card);
        clearErrors(account);
        sin.setErrors(sinValidator(sin));
      } else if(valueExists(dob)) {
        clearErrors(sin);
        clearErrors(dob);
        clearErrors(card);
        clearErrors(account);

        if(!moment(dob.value, 'YYYYMMDD', true).isValid()) {
          dob.setErrors({invalidDate: true});
        }
      } else if(valueExists(account) && !valueExists(card)) {
        clearErrors(sin);
        clearErrors(dob);
        clearErrors(card);
        clearErrors(account);
        card.setErrors({required: true});
      } else if(!valueExists(account) && valueExists(card)) {
        clearErrors(sin);
        clearErrors(dob);
        clearErrors(card);
        clearErrors(account);
        account.setErrors({required: true});
      } else {
        clearErrors(sin);
        clearErrors(dob);
        clearErrors(card);
        clearErrors(account);
      }
      if(!valueExists(this.form)) {
        clearErrors(firstName);
        clearErrors(lastName);
        clearErrors(street);
        clearErrors(city);
        clearErrors(province);
        clearErrors(postalCode);
        clearErrors(prevCity);
        clearErrors(prevProvince);
        clearErrors(prevPostalCode);
        clearErrors(sin);
        clearErrors(dob);
        clearErrors(card);
        clearErrors(account);
      }
      return;
    };
  }

  onChange: any = () => {};

  onTouched: any = () => {};

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  asIsOrder(a, b) {
    return -1;
  }

  registerOnChange(fn) {
    this.onChange = fn;
  }

  writeValue(value) {
    if(value) {
      this.value = value;
    }

    if(value === null) {
      this.form.reset();
    }
  }

  registerOnTouched(fn) {
    this.onTouched = fn;
  }

  validate(_: UntypedFormControl) {
    return this.form.valid ? null : {
      profile: {valid: false}
    };
  }

  get value(): Profile {
    return this.form.value;
  }

  set value(value: Profile) {
    this.form.setValue(value);
    this.onChange(value);
    this.onTouched();
  }

  get c(): {[p: string]: AbstractControl} {
    return this.form.controls;
  }
}
